/**
 * Grid Item Presets Registry
 *
 * Stores named row-template sets that can be referenced from any grid button
 * via `"itemsPreset": "<name>"` instead of duplicating inline `items` arrays
 * across every form that uses the same grid columns.
 *
 * Register once at app bootstrap (or in a plugin entry point):
 *
 *   import { GridItemPresetsRegistry } from '@atlas-forms/player-components-react';
 *
 *   GridItemPresetsRegistry.register('yesno', [
 *     { usage: 'condition_true',  port: 'condition_true',  label: 'Yes', isDefault: false },
 *     { usage: 'condition_false', port: 'condition_false', label: 'No',  isDefault: true  },
 *   ]);
 *
 * Then in any form JSON, reference by name instead of duplicating rows:
 *
 *   { "label": "Yes/No", "buttonAction": "addQuickItems", "itemsPreset": "yesno", "emoji": "✓✗" }
 *
 * Inline `items` on the button always takes priority over a preset, so existing
 * schemas continue to work without changes.
 */

export type GridItemPreset = Record<string, unknown>[];

const _presets = new Map<string, GridItemPreset>();

export const GridItemPresetsRegistry = {
  /**
   * Register a named set of row templates.
   * Overwrites any previously registered preset with the same name.
   */
  register(name: string, items: GridItemPreset): void {
    _presets.set(name, items);
  },

  /** Resolve a preset by name. Returns undefined if not registered. */
  get(name: string): GridItemPreset | undefined {
    return _presets.get(name);
  },

  /** Returns true if a preset is registered under this name. */
  has(name: string): boolean {
    return _presets.has(name);
  },

  /** Remove a named preset. */
  unregister(name: string): void {
    _presets.delete(name);
  },

  /** Remove all presets (useful for test teardown). */
  clear(): void {
    _presets.clear();
  },

  /** Return all registered preset names. */
  names(): string[] {
    return [..._presets.keys()];
  },
} as const;
